// src/modules/units/tyres/views/reports/tyre-reports-data.js
//
// Pure reporting logic: aggregate a list of tyres into a fleet report, and turn
// the list into CSV. No fetching or DOM — the orchestrator fetches the fleet and
// feeds it here, which keeps the maths testable in isolation.

// DISPLAY_STATUSES here (not QUERYABLE_*): this breakdown is computed in the
// browser from records we already hold, so it can show FITTED.
import { DISPLAY_STATUSES, statusLabel, statusTone, hasSensor, effectiveStatus } from '../../shared/tyre-status.js';
import { csvColumns, columnHeader, columnCsv } from '../../shared/tyre-columns.js';
import { remainingTreadPct, underinflatedPct, num } from '../../shared/tyre-metrics.js';

// Re-exported: remainingTreadPct moved to shared/tyre-metrics.js when the column
// registry needed it too (a shared module cannot import from a view). Kept
// exported from here so tyrereports.view.js and any other reader are unaffected.
export { remainingTreadPct };

const isBlank = (v) => v === null || v === undefined || v === '';

// Flags that surface a tyre in "Attention needed". Tunable in one place.
export const REPORT_THRESHOLDS = Object.freeze({
  treadCriticalPct: 20, // remaining tread below this → critical
  treadWatchPct: 40,    // …below this → watch
  treadAbsMm: 3,        // if no initial tread known, flag at/below this many mm
  underinflationPct: 15 // share of distance run under-inflated → watch
});

export function computeFleetReport(tyres = []) {
  const total = tyres.length;
  let fitted = 0;
  let inStock = 0;
  let totalPurchase = 0;
  let totalService = 0;
  let purchaseCount = 0;
  let totalKm = 0;
  let kmOver = 0, kmNormal = 0, kmUnder = 0, kmTempHigh = 0, kmTempNormal = 0;

  const statusCounts = {};
  const supplierMap = {};
  const appMap = {};
  const brandMap = {};
  const attention = [];

  for (const t of tyres) {
    if (hasSensor(t)) fitted++;
    if (t.tyreStatus === 'IN_STOCK') inStock++;

    // Group on the DERIVED status so the breakdown agrees with the badges.
    const s = effectiveStatus(t);
    statusCounts[s] = (statusCounts[s] || 0) + 1;

    const pc = num(t.purchaseCost);
    if (pc > 0) { totalPurchase += pc; purchaseCount++; }
    totalService += num(t.serviceDeliveryCost);
    if (t.supplier) {
      const m = supplierMap[t.supplier] || (supplierMap[t.supplier] = { supplier: t.supplier, count: 0, total: 0 });
      m.count++; m.total += pc;
    }

    totalKm += num(t.tyreKm);
    kmOver += num(t.tyreKmOverinflated);
    kmNormal += num(t.tyreKmNormal);
    kmUnder += num(t.tyreKmUnderinflated);
    kmTempHigh += num(t.tyreKmTemperatureHigh);
    kmTempNormal += num(t.tyreKmTemperatureNormal);

    const app = t.tyreApplication || '—';
    appMap[app] = (appMap[app] || 0) + 1;
    const brand = t.tyreBrand || t.tyreMake || '—';
    const bm = brandMap[brand] || (brandMap[brand] = { brand, count: 0, total: 0 });
    bm.count++; bm.total += pc;

    // attention flags
    const reasons = [];
    const pct = remainingTreadPct(t);
    if (pct != null) {
      if (pct < REPORT_THRESHOLDS.treadCriticalPct) reasons.push({ level: 'critical', text: `Tread ${Math.round(pct)}% left` });
      else if (pct < REPORT_THRESHOLDS.treadWatchPct) reasons.push({ level: 'watch', text: `Tread ${Math.round(pct)}% left` });
    } else if (!isBlank(t.tyreTreadDepth) && num(t.tyreTreadDepth) <= REPORT_THRESHOLDS.treadAbsMm) {
      reasons.push({ level: 'critical', text: `Tread ${num(t.tyreTreadDepth)} mm` });
    }
    const underPct = underinflatedPct(t);
    if (underPct !== null && underPct > REPORT_THRESHOLDS.underinflationPct) {
      reasons.push({ level: 'watch', text: `${Math.round(underPct)}% under-inflated` });
    }
    if (t.tyreStatus === 'MISSING') reasons.push({ level: 'critical', text: 'Missing' });
    if (reasons.length) attention.push({ tyre: t, reasons });
  }

  const byStatus = [];
  DISPLAY_STATUSES.forEach((s) => { if (statusCounts[s]) byStatus.push(mkStatus(s, statusCounts[s], total)); });
  Object.keys(statusCounts).forEach((s) => { if (!DISPLAY_STATUSES.includes(s)) byStatus.push({ status: s, label: s, tone: 'slate', count: statusCounts[s], pct: total ? (statusCounts[s] / total) * 100 : 0 }); });

  const bySupplier = Object.values(supplierMap).sort((a, b) => b.total - a.total);
  const byApplication = Object.entries(appMap).map(([application, count]) => ({ application, count })).sort((a, b) => b.count - a.count);
  const byBrand = Object.values(brandMap).sort((a, b) => b.count - a.count);

  const levelRank = { critical: 0, watch: 1 };
  attention.sort((a, b) => {
    const al = Math.min(...a.reasons.map((r) => levelRank[r.level] ?? 2));
    const bl = Math.min(...b.reasons.map((r) => levelRank[r.level] ?? 2));
    if (al !== bl) return al - bl;
    return (remainingTreadPct(a.tyre) ?? 999) - (remainingTreadPct(b.tyre) ?? 999);
  });

  return {
    total, fitted, inStock,
    cost: { totalPurchase, totalService, avgPurchase: purchaseCount ? totalPurchase / purchaseCount : 0, bySupplier },
    distance: {
      totalKm,
      pressure: { over: kmOver, normal: kmNormal, under: kmUnder },
      temperature: { high: kmTempHigh, normal: kmTempNormal }
    },
    byStatus, byApplication, byBrand,
    attention,
    attentionCounts: {
      critical: attention.filter((a) => a.reasons.some((r) => r.level === 'critical')).length,
      watch: attention.filter((a) => a.reasons.every((r) => r.level !== 'critical')).length
    }
  };
}

function mkStatus(s, count, total) {
  return { status: s, label: statusLabel(s), tone: statusTone(s), count, pct: total ? (count / total) * 100 : 0 };
}

// ---- CSV export ----------------------------------------------------------
//
// The column set, its order, its headings and its values all come from the shared
// registry now (shared/tyre-columns.js). This file keeps only the CSV mechanics:
// quoting, the header row, and the CRLF line terminator Excel expects.
//
// Two consequences worth knowing about, both deliberate:
//  - Status is the DERIVED status, as a label ("In Service"), matching the badge
//    on screen. It used to be the raw stored `tyreStatus` enum token, so a tyre
//    with a sensor and a stale IN_STOCK stamp exported a status that contradicted
//    the register.
//  - Model, Grade and Wear pattern are now exported. The create form has always
//    captured them; only the export could not emit them.

function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function tyresToCsv(tyres = []) {
  const cols = csvColumns();
  const header = cols.map((c) => csvCell(columnHeader(c, 'csv'))).join(',');
  const rows = tyres.map((t) => cols.map((c) => csvCell(columnCsv(c, t))).join(','));
  return [header, ...rows].join('\r\n');
}
